import React, { useRef } from 'react';
import { Box } from '@mui/material';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import { Analytics } from 'utils/analytics';
import { isNativeApp } from 'utils/nativeApp';
import styled from 'styled-components';

type CollabValue = 'favourite' | 'neutral' | 'block';

interface FutureCollaborationProps {
  value: CollabValue | null;
  firstName: string;
  onChange: (value: CollabValue) => void;
  /** T2-6961: display submitted preference without allowing changes */
  readOnly?: boolean;
}

const modalFont = "var(--fontMain, 'Poppins', sans-serif)";

const CollabContainer = styled(Box)`
  margin-bottom: 4px;
`;

const CollabTitle = styled.div`
  color: #6f7787;
  margin-bottom: 10px;
  font-size: 1rem;
  line-height: 1.35;
  font-weight: 400;
  font-family: ${modalFont};
`;

const OptionsRow = styled(Box)`
  display: flex;
  gap: 8px;
`;

interface CardProps {
  $selected: boolean;
}

const CollabCard = styled(Box)<CardProps & { $readOnly?: boolean }>`
  flex: 1;
  min-height: 82px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  padding: 12px 10px 10px;
  border-radius: 12px;
  border: 1px solid ${({ $selected }) => ($selected ? '#8faedd' : '#e4e8ef')};
  background-color: ${({ $selected }) => ($selected ? '#f5f9ff' : '#fff')};
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03);
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};

  &:hover {
    border-color: ${({ $selected, $readOnly }) => (
      $readOnly ? ($selected ? '#8faedd' : '#e4e8ef') : ($selected ? '#7fa4d8' : '#cfd7e6')
    )};
    box-shadow: ${({ $readOnly }) => ($readOnly ? '0 1px 2px rgba(16, 24, 40, 0.03)' : '0 2px 4px rgba(16, 24, 40, 0.05)')};
  }
`;

const OptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  font-size: 1.2rem;
  line-height: 1;
  margin-bottom: 8px;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
`;

const CardLabel = styled.div<CardProps>`
  max-width: 146px;
  color: #202939;
  font-size: 0.88rem;
  line-height: 1.3;
  font-weight: ${({ $selected }: CardProps) => ($selected ? 600 : 400)};
  letter-spacing: -0.01em;
  font-family: ${modalFont};
`;

const BlockWarning = styled.div`
  color: #FB7672;
  font-size: 0.82rem;
  line-height: 1.35;
  margin-top: 8px;
  font-family: ${modalFont};
`;

const FutureCollaboration: React.FC<FutureCollaborationProps> = ({
  value,
  firstName,
  onChange,
  readOnly = false,
}) => {
  const options: CollabValue[] = ['favourite', 'neutral', 'block'];
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Mobile (Cordova/WKWebView) can't render the emoji glyphs, so use MUI icons there.
  // Web renders fine, so keep the original emojis on web.
  const native = isNativeApp();

  const handleSelect = (option: CollabValue) => {
    if (readOnly) return;
    Analytics.track('collaboration preference', {
      preference: option,
      talentName: firstName,
    });
    onChange(option);
  };

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (readOnly) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(options[idx]); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault(); const next = (idx + 1) % options.length; handleSelect(options[next]); cardRefs.current[next]?.focus();
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault(); const prev = (idx - 1 + options.length) % options.length; handleSelect(options[prev]); cardRefs.current[prev]?.focus();
    }
  };

  return (
    <CollabContainer>
      <CollabTitle>
        Please tell us if you would like to work with {firstName} again
      </CollabTitle>

      <OptionsRow role="radiogroup" aria-label="Future collaboration preference" aria-disabled={readOnly || undefined}>
        <CollabCard
          ref={(el: HTMLDivElement | null) => { cardRefs.current[0] = el; }}
          $selected={value === 'favourite'}
          $readOnly={readOnly}
          role="radio"
          aria-checked={value === 'favourite'}
          aria-disabled={readOnly || undefined}
          tabIndex={readOnly ? -1 : (value === 'favourite' || !value ? 0 : -1)}
          onClick={() => handleSelect('favourite')}
          onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, 0)}
        >
          <OptionIcon>{native ? <FavoriteRoundedIcon style={{ fontSize: 24, color: '#FB4E66' }} /> : '♥️'}</OptionIcon>
          <CardLabel $selected={value === 'favourite'}>Favourite {firstName}, she was great!</CardLabel>
        </CollabCard>

        <CollabCard
          ref={(el: HTMLDivElement | null) => { cardRefs.current[1] = el; }}
          $selected={value === 'neutral'}
          $readOnly={readOnly}
          role="radio"
          aria-checked={value === 'neutral'}
          aria-disabled={readOnly || undefined}
          tabIndex={readOnly ? -1 : (value === 'neutral' ? 0 : -1)}
          onClick={() => handleSelect('neutral')}
          onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, 1)}
        >
          <OptionIcon>{native ? <SentimentSatisfiedAltRoundedIcon style={{ fontSize: 24, color: '#2E9E5B' }} /> : '😊'}</OptionIcon>
          <CardLabel $selected={value === 'neutral'}>{firstName} was good</CardLabel>
        </CollabCard>

        <CollabCard
          ref={(el: HTMLDivElement | null) => { cardRefs.current[2] = el; }}
          $selected={value === 'block'}
          $readOnly={readOnly}
          role="radio"
          aria-checked={value === 'block'}
          aria-disabled={readOnly || undefined}
          tabIndex={readOnly ? -1 : (value === 'block' ? 0 : -1)}
          onClick={() => handleSelect('block')}
          onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, 2)}
        >
          <OptionIcon>{native ? <SentimentDissatisfiedRoundedIcon style={{ fontSize: 24, color: '#FB7672' }} /> : '😔'}</OptionIcon>
          <CardLabel $selected={value === 'block'}>Block {firstName} from seeing our future shifts</CardLabel>
        </CollabCard>
      </OptionsRow>
      {value === 'block' && (
        <BlockWarning>
          This will prevent {firstName} from seeing your shifts. It is private and doesn't affect their account otherwise.
        </BlockWarning>
      )}
    </CollabContainer>
  );
};

export default FutureCollaboration;
